import {useContext, useEffect, useRef, useState} from "react";
import {AppsContext} from "../context/AppsContext.tsx"; // Adjust the import path as necessary
import "../CSS/components/Navbar.css"; // Create a separate CSS file for styling
import {useNavigate} from "react-router-dom";
import {appNameMapping} from "../CommonVariables.tsx";

import Button from "@mui/material/Button";
import LogoutIcon from "@mui/icons-material/Logout";

import {useCurrentApp} from "../context/CurrentAppContext.tsx";
import {DefaultButton} from "./Buttons.tsx";

const isAppName = (key: string): key is keyof typeof appNameMapping => {
    return key in appNameMapping;
};
// if you want the user to confirm clicking away from his current program via the navbar
// set showWarningWhenClicked = true
const Navbar = ({showWarningWhenClicked = false}) => {
    const context = useContext(AppsContext);
    //let showWarningWhenClicked = true;
    if (!context) {
        throw new Error("Navbar must be used within an AppsProvider");
    }
    const {apps} = context;
    const navigate = useNavigate();
    const {currentApp, setCurrentApp} = useCurrentApp();

    // For dragging and scrolling
    const navbarListRef = useRef<HTMLUListElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isClickCancelled, setIsClickCancelled] = useState(false);

    // Makes the user confirm his click
    const handleNavigationCheck = (e: React.MouseEvent, message:string) => {
        if (showWarningWhenClicked) {
            const proceed = window.confirm(message);
            if (!proceed) {
                if(e) e.preventDefault();
                return false;
            } else {
                return true;
            }
        } else {
            return true;    //if handleNavigationCheck is false we return true right away
        }
    }

    const logout = () => {
        const CheckMessage = "Bist du dir sicher, dass du dich abmelden willst?\n" +
            "Deine bisherigen Eingaben gehen dann verloren."
        const proceed = handleNavigationCheck(null, CheckMessage);
        if(proceed){
            navigate("/");
            localStorage.removeItem("token");
            window.location.reload();
        }else{
            return;
        }
    };


    const renderLogoutButton = () => {
        return (
            <DefaultButton
                label={"ABMELDEN"}
                variant="contained"
                size="small"
                color="red"
                onClick={() => logout()}
                endIcon={<LogoutIcon/>}
                sx={{
                    minWidth: 'auto',
                    width: 'auto',
                    paddingLeft: '15px',
                    paddingRight: '15px',
                    marginLeft: '15px',
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    borderRadius: '5px',
                    height: '100%'
                }}
            />
        );
    };

    const gotoProgram = (programName: string) => {
        setCurrentApp(programName); // from CurrentAppContext

        if (programName === "OnlineOffline_Artikel") {
            // isAuthenticated();
            navigate("/OnOffArticle");
        } else if (programName === "OnlineOffline_Liste") {
            // isAuthenticated();
            navigate("/OnOffList");
        } else if (programName === "ItemPricing") {
            navigate("/ItemPricing");
        } else if (programName === "OrderProcessing") {
            navigate("/OrderProcessing");
        } else if (programName === "LabelPrinter") {
            navigate("/LabelPrinter");
        } else if (programName === "ItemCreator") {
            navigate("/ItemCreator");
        } else if (programName === "AdminPanel") {
            navigate("/AdminPanel");
        } else if (programName === "ListingCreator"){
            navigate("/ListingCreator");
        }
        else {
            alert("Redirecting to " + programName);
        }
    };

    const LogoClick = () => {
        const checkMessage = "Bist du dir sicher, dass du in die Programmübersicht wechseln willst?\n" +
            "Deine bisherigen Eingaben gehen dann verloren."
        const proceed = handleNavigationCheck(null, checkMessage);
        if(proceed) {
            setCurrentApp(undefined); // from CurrentAppContext to reset color of currently used app
            navigate("/ProgramOverview");
        }else{
            return;
        }
    };

    // Handle drag-to-scroll
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();

        setIsDragging(false);
        setIsClickCancelled(false);

        const slider = navbarListRef.current;
        if (!slider) return;

        slider.style.cursor = "grabbing";
        slider.dataset.isDown = "true";
        slider.dataset.startX = String(e.pageX - slider.offsetLeft);
        slider.dataset.scrollLeft = String(slider.scrollLeft);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        const slider = navbarListRef.current;
        if (!slider || slider.dataset.isDown !== "true") return;

        const scroll_speed = 1;
        const x = e.pageX - slider.offsetLeft;
        const walk = (x - Number(slider.dataset.startX)) * scroll_speed; // Scroll speed
        slider.scrollLeft = Number(slider.dataset.scrollLeft) - walk;

        // Set dragging to true if there's movement
        setIsDragging(true);
        // Delay resetting `isClickCancelled` to prevent accidental clicks
        setTimeout(() => {
            setIsClickCancelled(false); // Ensure clicks can be registered
        }, 150); // Adjust delay time if necessary
        e.preventDefault(); // Prevent text selection
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        const slider = navbarListRef.current;
        if (!slider) return;

        slider.style.cursor = "grab";
        slider.dataset.isDown = "false";

        if (isDragging) {
            setIsClickCancelled(true);
            e.preventDefault();
        }

        setIsDragging(false); // Reset dragging state
    };

    // Handle scrolling this way because scrolling is otherwise a passive thing and
    // Scrolls the whole page up and down instead of just the navbar left and right
    useEffect(() => {
        const handleScrollWheel = (event: WheelEvent) => {
            if (navbarListRef.current) {
                event.preventDefault(); // Prevent default scroll behavior
                navbarListRef.current.scrollLeft += event.deltaY * 3; // Adjust horizontal scroll
            }
        };

        const element = navbarListRef.current;
        element?.addEventListener("wheel", handleScrollWheel, {passive: false});

        return () => {
            element?.removeEventListener("wheel", handleScrollWheel);
        };
    }, []);


    const handleNavItemClick = (e: React.MouseEvent, applicationName: string) => {
        if (isDragging || isClickCancelled) {
            e.preventDefault(); // Cancel the click if dragging occurred
            return;
        }
        const CheckMessage = "Bist du dir sicher, dass du das Programm wechseln willst?\n" +
            "Deine bisherigen Eingaben gehen dann verloren."
        const proceed = handleNavigationCheck(e, CheckMessage);
        if (proceed) {
            gotoProgram(applicationName);
        } else {
            return;
        }
    }

    return (
        <nav
            className="navbar"
        >
            <img onClick={() => LogoClick()} src="/logo-lanatura.png"/>
            <ul
                className="navbar-list"
                ref={navbarListRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseUp}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
            >
                {apps.map((app, index) => (
                    <li key={index}>
                        <Button
                            variant="text"
                            onClick={(e) => handleNavItemClick(e, app.application)}
                            className={currentApp === app.application ? "active" : ""}
                        >
                            {" "}
                            {isAppName(app.application)
                                ? appNameMapping[app.application]
                                : app.application}{" "}
                        </Button>
                    </li>
                ))}
            </ul>
            {renderLogoutButton()}
        </nav>
    );
};

export default Navbar;
